import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ShoppingCart, Heart, Star, Shield, Truck, ChevronRight, Minus, Plus, Package, CheckCircle, Info, AlertTriangle, FileText, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetail() {
  const { id } = useParams();
  const { user, addToCart: contextAddToCart } = useAuth();
  const [product, setProduct] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [reviewText, setReviewText] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/products/${id}`).then(r => {
      setProduct(r.data);
      axios.get(`${API}/products?category=${encodeURIComponent(r.data.category)}&limit=8`).then(s => setSimilar(s.data.products?.filter(p => p.product_id !== id) || []));
    }).catch(() => toast.error("Product not found")).finally(() => setLoading(false));
    axios.get(`${API}/reviews/${id}`).then(r => setReviews(r.data.reviews || [])).catch(() => {});
  }, [id]);

  const handleAddToCart = async () => {
    try {
      await contextAddToCart(product, quantity);
      toast.success("Added to cart!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const submitReview = async () => {
    if (!user) { toast.error("Please login to write a review"); return; }
    try {
      const res = await axios.post(`${API}/reviews/${id}`, { rating: reviewRating, text: reviewText }, { withCredentials: true });
      setReviews([res.data, ...reviews]);
      setReviewText("");
      toast.success("Review submitted!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-12"><div className="animate-pulse grid lg:grid-cols-2 gap-8"><div className="aspect-square bg-slate-200 rounded-2xl" /><div className="space-y-4"><div className="h-4 bg-slate-200 rounded w-1/3" /><div className="h-8 bg-slate-200 rounded w-2/3" /><div className="h-6 bg-slate-200 rounded w-1/4" /></div></div></div>;
  if (!product) return <div className="text-center py-20 text-slate-500">Product not found</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="product-detail-page">
      <nav className="flex items-center gap-2 text-sm text-slate-500 mb-6">
        <Link to="/" className="hover:text-cyan-600">Home</Link><ChevronRight className="w-3 h-3" />
        <Link to={`/products?category=${encodeURIComponent(product.category)}`} className="hover:text-cyan-600">{product.category}</Link><ChevronRight className="w-3 h-3" />
        <span className="text-slate-800 font-medium truncate">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-8 mb-12">
        {/* Image */}
        <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100">
          <div className="aspect-square rounded-xl overflow-hidden bg-slate-50">
            <img src={product.images?.[0] || ""} alt={product.name} className="w-full h-full object-cover" data-testid="product-image" />
          </div>
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className="text-cyan-600 border-cyan-200 text-xs">{product.brand}</Badge>
            {product.requires_prescription && <Badge className="bg-amber-500 text-white text-xs"><Shield className="w-3 h-3 mr-1" />Prescription Required</Badge>}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight mb-2" data-testid="product-name">{product.name}</h1>
          <p className="text-sm text-slate-500 mb-2">{product.composition}</p>

          {/* Composition Badge */}
          {product.composition && (
            <div className="bg-sky-50 border border-sky-100 rounded-xl p-3 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <FlaskConical className="w-4 h-4 text-cyan-600" />
                <span className="text-xs font-semibold text-cyan-700">Salt Composition</span>
              </div>
              <p className="text-sm text-slate-700 font-medium">{product.composition}</p>
            </div>
          )}

          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center gap-1 bg-emerald-50 px-2 py-1 rounded-full">
              <Star className="w-4 h-4 fill-emerald-500 text-emerald-500" />
              <span className="text-sm font-semibold text-emerald-700">{product.rating}</span>
            </div>
            <span className="text-sm text-slate-500">{product.review_count} reviews</span>
          </div>

          <div className="flex items-end gap-3 mb-6">
            <span className="text-3xl font-bold text-slate-800" data-testid="product-price">Rs.{product.sale_price}</span>
            {product.mrp > product.sale_price && <span className="text-lg text-slate-400 line-through">Rs.{product.mrp}</span>}
            {product.discount_percent > 0 && <Badge className="bg-emerald-500 text-white text-sm">{product.discount_percent}% OFF</Badge>}
          </div>

          {product.pack_sizes?.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-medium text-slate-700 mb-2">Pack Size</p>
              <div className="flex flex-wrap gap-2">
                {product.pack_sizes.map((ps, i) => (
                  <div key={i} className="px-4 py-2 border border-cyan-200 rounded-xl text-sm bg-cyan-50 text-cyan-700 font-medium">{ps.size}</div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mb-6">
            <p className="text-sm font-medium text-slate-700">Quantity:</p>
            <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-3 py-2 hover:bg-slate-50"><Minus className="w-4 h-4" /></button>
              <span className="px-4 py-2 text-sm font-medium border-x border-slate-200" data-testid="product-quantity">{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)} className="px-3 py-2 hover:bg-slate-50"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          <div className={`flex items-center gap-2 mb-6 ${product.in_stock ? 'text-emerald-600' : 'text-red-500'}`}>
            <Package className="w-4 h-4" />
            <span className="text-sm font-medium">{product.in_stock ? `In Stock (${product.stock_qty} units)` : 'Out of Stock'}</span>
          </div>

          <div className="flex gap-3 mb-6">
            <Button onClick={handleAddToCart} disabled={!product.in_stock} className="flex-1 bg-cyan-600 hover:bg-cyan-700 rounded-full py-3 font-semibold" data-testid="add-to-cart-btn">
              <ShoppingCart className="w-4 h-4 mr-2" /> Add to Cart
            </Button>
            <Button onClick={() => { handleAddToCart().then(() => navigate("/cart")); }} variant="outline" className="rounded-full px-6 border-cyan-200 text-cyan-700 hover:bg-cyan-50" data-testid="buy-now-btn">
              Buy Now
            </Button>
          </div>

          <div className="flex items-center gap-2 text-sm text-slate-500 bg-sky-50 p-3 rounded-xl">
            <Truck className="w-4 h-4 text-cyan-600" />
            <span>Free delivery on orders above Rs.499</span>
          </div>
        </div>
      </div>

      {/* Customers Also Bought */}
      {similar.length > 0 && (
        <section className="mb-8" data-testid="also-bought-section">
          <h2 className="text-lg font-bold text-slate-800 mb-4">Customers Also Bought</h2>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {similar.map(p => (
              <Link key={p.product_id} to={`/products/${p.product_id}`} className="flex-shrink-0 w-44 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 overflow-hidden hover:shadow-lg transition-all">
                <div className="aspect-square bg-slate-50 overflow-hidden"><img src={p.images?.[0] || ""} alt={p.name} className="w-full h-full object-cover" loading="lazy" /></div>
                <div className="p-3">
                  <p className="text-xs font-semibold text-slate-800 line-clamp-2">{p.name}</p>
                  <div className="flex items-center justify-between mt-2">
                    <div><p className="text-sm font-bold text-slate-800">Rs.{p.sale_price}</p>{p.mrp > p.sale_price && <p className="text-[10px] text-slate-400 line-through">Rs.{p.mrp}</p>}</div>
                    {p.discount_percent > 0 && <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">{p.discount_percent}%</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Tabs - Enhanced with more sections */}
      <Tabs defaultValue="description" className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 mb-12">
        <TabsList className="bg-sky-50 rounded-xl flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="description" className="rounded-xl text-xs">Description</TabsTrigger>
          <TabsTrigger value="keyuses" className="rounded-xl text-xs">Key Uses</TabsTrigger>
          <TabsTrigger value="howtouse" className="rounded-xl text-xs">How to Use</TabsTrigger>
          <TabsTrigger value="sideeffects" className="rounded-xl text-xs">Side Effects</TabsTrigger>
          <TabsTrigger value="safety" className="rounded-xl text-xs">Safety Info</TabsTrigger>
          <TabsTrigger value="reviews" className="rounded-xl text-xs">Reviews ({reviews.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="description" className="mt-4">
          <div className="flex items-center gap-2 mb-3"><div className="w-8 h-8 rounded-lg bg-cyan-50 flex items-center justify-center"><FileText className="w-4 h-4 text-cyan-600" /></div><h3 className="font-semibold text-slate-800">Product Description</h3></div>
          <p className="text-sm text-slate-600 leading-relaxed mb-4">{product.description}</p>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {product.manufacturer && <div className="bg-sky-50 rounded-xl p-3"><p className="text-[10px] text-slate-500 uppercase tracking-wider">Manufacturer</p><p className="text-sm font-medium text-slate-800">{product.manufacturer}</p></div>}
            {product.form && <div className="bg-sky-50 rounded-xl p-3"><p className="text-[10px] text-slate-500 uppercase tracking-wider">Form</p><p className="text-sm font-medium text-slate-800">{product.form}</p></div>}
            {product.composition && <div className="bg-sky-50 rounded-xl p-3"><p className="text-[10px] text-slate-500 uppercase tracking-wider">Composition</p><p className="text-sm font-medium text-slate-800">{product.composition}</p></div>}
            {product.category && <div className="bg-sky-50 rounded-xl p-3"><p className="text-[10px] text-slate-500 uppercase tracking-wider">Category</p><p className="text-sm font-medium text-slate-800">{product.category} / {product.subcategory}</p></div>}
          </div>
          {product.tags?.length > 0 && <div className="mt-4"><p className="text-xs font-semibold text-slate-600 mb-2">Tags</p><div className="flex flex-wrap gap-2">{product.tags.map(t => <span key={t} className="px-2.5 py-1 bg-slate-100 rounded-full text-xs text-slate-600">{t}</span>)}</div></div>}
        </TabsContent>
        <TabsContent value="keyuses" className="mt-4">
          <div className="flex items-center gap-2 mb-3"><div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center"><CheckCircle className="w-4 h-4 text-emerald-600" /></div><h3 className="font-semibold text-slate-800">Key Uses of {product.name}</h3></div>
          <ul className="space-y-3">
            {product.key_uses ? product.key_uses.split('\n').filter(Boolean).map((line, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />{line}</li>
            )) : <>
            <li className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />Used in the treatment and prevention of conditions related to its composition ({product.composition || 'N/A'})</li>
            <li className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />Helps maintain optimal health levels as directed by your physician</li>
            <li className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />Supports overall wellbeing when taken as part of prescribed treatment</li>
            <li className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />Available in {product.form} form for easy consumption</li>
            </>}
          </ul>
        </TabsContent>
        <TabsContent value="howtouse" className="mt-4">
          <div className="flex items-center gap-2 mb-3"><div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center"><Info className="w-4 h-4 text-indigo-600" /></div><h3 className="font-semibold text-slate-800">How to Use</h3></div>
          <ul className="space-y-3">
            {product.how_to_use ? product.how_to_use.split('\n').filter(Boolean).map((line, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold shrink-0">{i+1}</span>{line}</li>
            )) : <>
            <li className="flex items-start gap-2 text-sm text-slate-600"><span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold shrink-0">1</span>Take as directed by your healthcare professional or as mentioned on the label</li>
            <li className="flex items-start gap-2 text-sm text-slate-600"><span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold shrink-0">2</span>Do not exceed the recommended dosage unless advised by your doctor</li>
            <li className="flex items-start gap-2 text-sm text-slate-600"><span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold shrink-0">3</span>{product.form === "Tablet" || product.form === "Capsule" ? "Swallow whole with water. Do not crush or chew." : product.form === "Syrup" || product.form === "Liquid" ? "Shake well before use. Use the measuring cup provided." : "Use as directed on the packaging or by your doctor."}</li>
            </>}
            {product.requires_prescription && <li className="flex items-start gap-2 text-sm text-red-600 font-medium"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />This medicine requires a valid prescription from a registered medical practitioner.</li>}
          </ul>
        </TabsContent>
        <TabsContent value="sideeffects" className="mt-4">
          <div className="flex items-center gap-2 mb-3"><div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-amber-600" /></div><h3 className="font-semibold text-slate-800">Possible Side Effects</h3></div>
          <p className="text-sm text-slate-500 mb-3">Common side effects may include (not everyone gets these):</p>
          <ul className="space-y-2">
            {["Nausea or stomach upset", "Mild headache or dizziness", "Allergic reactions (rare) - seek medical help immediately", "Consult your doctor if any side effects persist or worsen"].map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />{s}</li>
            ))}
          </ul>
          <div className="mt-4 p-3 bg-amber-50 rounded-xl text-xs text-amber-800">This is not a complete list of side effects. Please consult your doctor or pharmacist for personalized advice.</div>
        </TabsContent>
        <TabsContent value="safety" className="mt-4">
          <div className="flex items-center gap-2 mb-3"><div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center"><Shield className="w-4 h-4 text-red-600" /></div><h3 className="font-semibold text-slate-800">Safety Information</h3></div>
          <div className="space-y-3">
            {[
              { label: "Storage", text: "Store in a cool, dry place below 30°C. Protect from direct sunlight and moisture." },
              { label: "Children", text: "Keep out of reach of children. Not recommended for children unless prescribed." },
              { label: "Pregnancy", text: "Consult your doctor before taking this medicine if pregnant or breastfeeding." },
              { label: "Alcohol", text: "Interaction with alcohol is not fully known. Consult your doctor before consumption." },
              { label: "Driving", text: "May cause drowsiness in some. Exercise caution while driving or operating machinery." },
            ].map((s, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                <span className="text-xs font-bold text-slate-700 w-20 shrink-0">{s.label}</span>
                <span className="text-sm text-slate-600">{s.text}</span>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="reviews" className="mt-4">
          {user && (
            <div className="mb-6 p-4 bg-sky-50 rounded-xl">
              <h4 className="text-sm font-semibold text-slate-800 mb-3">Write a Review</h4>
              <div className="flex gap-1 mb-3">{[1,2,3,4,5].map(s => (<button key={s} onClick={() => setReviewRating(s)}><Star className={`w-5 h-5 ${s <= reviewRating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} /></button>))}</div>
              <Textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder="Share your experience with this product..." className="mb-3 rounded-xl" data-testid="review-text-input" />
              <Button onClick={submitReview} size="sm" className="rounded-full bg-cyan-600 hover:bg-cyan-700" data-testid="submit-review-btn">Submit Review</Button>
            </div>
          )}
          <div className="space-y-4">
            {reviews.map(r => (
              <div key={r.review_id} className="border-b border-slate-100 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 rounded-full bg-cyan-100 flex items-center justify-center text-xs font-bold text-cyan-700">{r.user_name?.[0]}</div>
                  <span className="text-sm font-semibold text-slate-800">{r.user_name}</span>
                  <div className="flex">{[1,2,3,4,5].map(s => <Star key={s} className={`w-3 h-3 ${s <= r.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />)}</div>
                </div>
                <p className="text-sm text-slate-600 ml-9">{r.text}</p>
              </div>
            ))}
            {reviews.length === 0 && <p className="text-sm text-slate-500">No reviews yet. Be the first to review this product!</p>}
          </div>
        </TabsContent>
      </Tabs>

      {/* Manufacturer Details */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 mb-8">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Manufacturer Details</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><p className="text-slate-500">Manufacturer</p><p className="font-medium text-slate-800">{product.manufacturer || product.brand}</p></div>
          <div><p className="text-slate-500">Country of Origin</p><p className="font-medium text-slate-800">India</p></div>
          <div><p className="text-slate-500">Storage</p><p className="font-medium text-slate-800">Store below 30°C</p></div>
          <div><p className="text-slate-500">Authenticity</p><p className="font-medium text-emerald-600">100% Genuine Product</p></div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-slate-50 rounded-2xl p-5 mb-12 text-xs text-slate-500 leading-relaxed">
        <p className="font-semibold text-slate-700 mb-1">Disclaimer</p>
        The information provided is for educational purposes only and should not be considered medical advice. Always consult a healthcare professional before starting any medication. Product images are for reference only and may differ from actual product. Pharma Palace guarantees 100% genuine products sourced from licensed manufacturers.
      </div>

      {/* Similar Products */}
      {similar.length > 0 && (
        <section className="mb-12">
          <h2 className="text-xl font-bold text-slate-800 mb-6">Similar Products</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {similar.slice(0, 4).map(p => <ProductCard key={p.product_id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
